import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
  setDoc,
  orderBy,
  limit,
  type QueryDocumentSnapshot,
  type Firestore,
  type Timestamp,
} from 'firebase/firestore';
import { nanoid } from 'nanoid';
import { ensureAuth } from '../lib/auth';
import { auth, db } from '@/lib/firebase';
import { checkOwnerOrAdmin } from '@/lib/authz';
import { isDebugEnabled } from '@/lib/debugFlag';
import { QUIZZES_COLL } from './constants';
export { QUIZZES_COLL } from './constants';

export type Quiz = {
  id: string;
  title: string;
  description?: string;
  ownerId: string;
  createdAt: Timestamp;
  updatedAt?: Timestamp;
  status?: string;
  [k: string]: unknown;
};

export type QuizSummary = {
  id: string;
  title: string;
  state: "draft" | "open" | "closed";
  ownerId?: string;
  createdAt?: Timestamp;
  [k: string]: unknown;
};

function mapDoc(d: QueryDocumentSnapshot): QuizSummary {
  const data = d.data() as any;
  const title = typeof data.title === 'string' ? data.title : '(untitled)';
  const state = (data.state as any) ?? 'draft';
  const result: QuizSummary = {
    id: d.id,
    title,
    state,
  };
  if (data.ownerId) result.ownerId = data.ownerId;
  if (data.createdAt) result.createdAt = data.createdAt;
  return result;
}

// Shape that we persist (adjust fields to match the app's Quiz type)
function debugQuiz(tag: string, payload?: unknown): void {
  if (isDebugEnabled()) {
    // eslint-disable-next-line no-console
    console.info('[QuickQuiz]', tag, payload);
  }
}

export function getQuizRef(db: Firestore, quizId: string) {
  return doc(collection(db, QUIZZES_COLL), quizId);
}

interface ListOptions {
  ownerId?: string;
}

// List ALL quizzes (used by debug panel / admin views)
export async function listAllQuizzes(db: Firestore, max = 50): Promise<QuizSummary[]> {
  const coll = collection(db, QUIZZES_COLL);
  const q = query(coll, orderBy('createdAt', 'desc'), limit(max));
  const snap = await getDocs(q);
  return snap.docs.map(mapDoc);
}

// List current user's quizzes (used by "My Quizzes")
export async function listMyQuizzes(
  db: Firestore,
  uid?: string,
  max = 50
): Promise<QuizSummary[]> {
  // Require explicit UID - don't auto-sign in
  if (!uid && !auth.currentUser) {
    console.warn('[listMyQuizzes] No user signed in, returning empty list');
    return [];
  }
  const userId = uid || auth.currentUser!.uid;
  const coll = collection(db, QUIZZES_COLL);
  const q = query(
    coll,
    where('ownerId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(max)
  );
  const snap = await getDocs(q);
  return snap.docs.map(mapDoc);
}

export async function listQuizzes(
  db: Firestore,
  opts: ListOptions = {}
): Promise<QuizSummary[]> {
  if (opts.ownerId) {
    return listMyQuizzes(db, opts.ownerId);
  }
  return listAllQuizzes(db);
}

export function subscribeQuizzes(opts: {
  ownerId?: string | null;
  onUpdate: (quizzes: QuizSummary[]) => void;
  onError?: (err: unknown) => void;
}): () => void {
  const { ownerId, onUpdate, onError } = opts;
  if (!ownerId) {
    queueMicrotask(() => onUpdate([]));
    return () => {};
  }
  let unsub: (() => void) | undefined;
  try {
    const q = query(collection(db, 'quizzes'), where('ownerId', '==', ownerId));
    unsub = onSnapshot(
      q,
      (snap) => {
        if (!snap || snap.empty) {
          onUpdate([]);
          return;
        }
        try {
          const items: QuizSummary[] = [];
          snap.forEach((d) => {
            const data = d.data() as any;
            if (typeof data.title !== 'string' || typeof data.state !== 'string') return;
            const item: QuizSummary = {
              id: d.id,
              title: data.title,
              state: data.state as any,
            };
            if (data.ownerId) item.ownerId = data.ownerId;
            if (data.createdAt) item.createdAt = data.createdAt;
            items.push(item);
          });
          onUpdate(items);
        } catch (e) {
          console.warn('[subscribeQuizzes] snapshot parse error', e);
          onUpdate([]);
        }
      },
      (err) => {
        console.warn('[subscribeQuizzes] Firestore error', err);
        onError?.(err);
      }
    );
  } catch (err) {
    console.warn('[subscribeQuizzes] setup error', err);
    queueMicrotask(() => onUpdate([]));
    onError?.(err);
    return () => {};
  }
  let called = false;
  return () => {
    if (called) return;
    called = true;
    try {
      unsub && unsub();
    } catch (err) {
      console.warn('[subscribeQuizzes] unsubscribe error', err);
    }
  };
}

export async function listQuizzesByOwner(ownerId: string): Promise<QuizSummary[]> {
  if (!ownerId) return [];
  try {
    const q = query(collection(db, 'quizzes'), where('ownerId', '==', ownerId));
    const snap = await getDocs(q);
    if (snap.empty) return [];
    const out: QuizSummary[] = [];
    snap.forEach((d) => {
      try {
        const data = d.data() as any;
        if (typeof data.title !== 'string' || typeof data.state !== 'string') return;
        const item: QuizSummary = {
          id: d.id,
          title: data.title,
          state: data.state as any,
        };
        if (data.ownerId) item.ownerId = data.ownerId;
        if (data.createdAt) item.createdAt = data.createdAt;
        out.push(item);
      } catch (e) {
        console.warn('[listQuizzesByOwner] bad doc', e);
      }
    });
    return out;
  } catch (err) {
    console.warn('[listQuizzesByOwner] Firestore error', err);
    return [];
  }
}

export async function deleteQuizById(
  id: string,
  ownerId?: string | null,
  opts?: { currentUid?: string | null; isAdmin?: boolean },
) {
  const ok = checkOwnerOrAdmin({
    ownerId,
    currentUid: opts?.currentUid ?? auth.currentUser?.uid ?? null,
    isAdmin: opts?.isAdmin ?? false,
    context: 'deleteQuiz',
  });
  if (!ok) console.warn('[quizzes] proceeding with delete; Firestore rules will enforce', { id, ownerId });
  const ref = doc(db, QUIZZES_COLL, id);
  try {
    await deleteDoc(ref);
    console.info('[quizzes] deleted', { id });
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      console.debug('[quizzes] delete permission-denied (likely race)', { id });
    } else {
      throw err;
    }
  }
}

export type QuizState = 'draft' | 'ready' | 'active' | 'ended' | 'in_progress';

export async function createQuiz(params: {
  title: string;
  description?: string;
  initialState?: QuizState;
}): Promise<string> {
  // Require explicit authentication - user must be signed in to create quiz
  const currentUser = auth.currentUser;
  if (!currentUser?.uid) {
    throw new Error('auth-required: User must be signed in to create a quiz');
  }
  
  const coll = collection(db, QUIZZES_COLL);
  const ref = doc(coll);
  const payload = {
    title: params.title?.trim() || 'Untitled Quiz',
    description: params.description?.trim() || '',
    state: params.initialState ?? 'draft',
    ownerId: currentUser.uid,      // For querying (legacy)
    createdBy: currentUser.uid,    // For security rules
    createdAt: serverTimestamp(),
    currentQuestionIndex: 0,
  };
  await setDoc(ref, payload);
  return ref.id;
}

export async function createRandomQuizForTesting(): Promise<string> {
  // Import dynamically to avoid circular dependencies
  const { fetchRandomQuestions, generateQuizTitle } = await import('../services/openTriviaService');
  
  let questions;
  let title = `General Knowledge Trivia — ${nanoid(5)}`;
  
  try {
    // Try to fetch random questions from Open Trivia DB
    questions = await fetchRandomQuestions({ 
      amount: 10, // Get 10 questions
      type: 'multiple' // Multiple choice only
    });
    
    // Generate a better title based on the questions
    if (questions.length > 0) {
      title = generateQuizTitle(questions);
    }
    
    console.debug('[createRandomQuiz] Successfully fetched questions from OpenTriviaDB');
  } catch (error) {
    console.warn('[createRandomQuiz] Failed to fetch from OpenTriviaDB, using fallback questions', error);
    
    // Fallback to hardcoded questions if API fetch fails
    questions = [
      {
        prompt: "What is the capital of France?",
        answers: [
          { text: "Paris", isCorrect: true },
          { text: "London", isCorrect: false },
          { text: "Berlin", isCorrect: false },
          { text: "Rome", isCorrect: false }
        ],
        timeLimit: 30
      },
      {
        prompt: "Who wrote 'Romeo and Juliet'?",
        answers: [
          { text: "William Shakespeare", isCorrect: true },
          { text: "Charles Dickens", isCorrect: false },
          { text: "Jane Austen", isCorrect: false },
          { text: "Mark Twain", isCorrect: false }
        ],
        timeLimit: 30
      },
      {
        prompt: "What is 2 + 2?",
        answers: [
          { text: "3", isCorrect: false },
          { text: "4", isCorrect: true },
          { text: "5", isCorrect: false },
          { text: "22", isCorrect: false }
        ],
        timeLimit: 20
      }
    ];
  }
  
  // Create the quiz in Firestore
  const id = await createQuiz({
    title,
    description: 'Auto-generated quiz from Open Trivia Database',
    initialState: 'draft',
  });
  
  // Get the current user (must be authenticated to create quiz)
  const currentUser = auth.currentUser;
  if (!currentUser?.uid) {
    throw new Error('auth-required: User must be signed in to create a random quiz');
  }
  
  // Update the quiz with the fetched or fallback questions
  const ref = getQuizRef(db, id);
  await updateDoc(ref, { 
    questions,
    updatedAt: serverTimestamp()
  });
  
  console.debug('[Host] created random quiz with sample questions:', id);
  return id;
}

export async function updateQuiz(
  db: Firestore,
  quizId: string,
  patch: Partial<Quiz>,
  authUser: { uid: string }
): Promise<void> {
  const ref = getQuizRef(db, quizId);
  const snap = await getDoc(ref);
  if (!snap.exists() || (snap.data() as any).ownerId !== authUser.uid) {
    throw new Error('not_owner');
  }
  await updateDoc(ref, { ...patch, updatedAt: serverTimestamp() });
  debugQuiz('updateQuiz', { id: quizId });
}

export async function deleteQuiz(
  db: Firestore,
  quizId: string,
  authUser: { uid: string }
): Promise<void> {
  const ref = getQuizRef(db, quizId);
  const snap = await getDoc(ref);
  if (!snap.exists() || (snap.data() as any).ownerId !== authUser.uid) {
    throw new Error('not_owner');
  }
  try {
    await deleteDoc(ref);
    debugQuiz('deleteQuiz', { id: quizId });
  } catch (err: any) {
    if (err?.code === 'permission-denied') {
      console.debug('[quizzes] delete permission-denied (likely race)', { id: quizId });
    } else {
      throw err;
    }
  }
}

/**
 * Legacy helper to migrate quizzes from `users/{uid}/quizzes` to `/quizzes`.
 */
export async function migrateUserSubcollectionToRoot(db: Firestore, uid: string): Promise<void> {
  const legacy = collection(db, 'users', uid, 'quizzes');
  const snaps = await getDocs(legacy);
  for (const d of snaps.docs) {
    const data = d.data() as any;
    const id = d.id;
    const dest = getQuizRef(db, id);
    const existing = await getDoc(dest);
    if (!existing.exists()) {
      await setDoc(dest, {
        ...data,
        ownerId: uid,
        createdAt: data.createdAt ?? serverTimestamp(),
      });
      debugQuiz('migrateQuiz', { id });
    }
  }
}


